import { apiGet, apiPost } from '@/lib/api';
import {
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  SetPasswordRequest,
  ChangePasswordRequest,
  UserResponse,
} from '@troia-v3/types';
import { detectBrowser } from '@/lib/browser';
import { collectClientHints } from '@/lib/clientHints';
import { getOrCreateDeviceId } from '@/lib/deviceId';

export interface RegisterRequest {
  name: string;
  email: string;
  phone?: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  user: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  };
}

export class AuthService {
  private static readonly BASE_URL = '/users';

  // Register
  static async register(data: RegisterRequest, signal?: AbortSignal): Promise<LoginResponse> {
    return apiPost<LoginResponse>('/companies/register', data, { signal });
  }

  // Login — envia device identity junto com as credenciais (Phase B)
  static async login(credentials: LoginRequest, signal?: AbortSignal): Promise<LoginResponse> {
    const deviceId = getOrCreateDeviceId();
    const browser = detectBrowser();
    // Client Hints (Chromium): versão real do SO e modelo do aparelho — o
    // User-Agent congelou esses campos. Ausentes em Safari/Firefox, aí o
    // backend classifica só pelo User-Agent.
    const clientHints = await collectClientHints();
    const body: LoginRequest = {
      ...credentials,
      device: {
        deviceId,
        platform: 'web' as const,
        browser,
        ...(clientHints ? { clientHints } : {})
      }
    };
    return apiPost<LoginResponse>(`${this.BASE_URL}/login`, body, { signal });
  }

  // Forgot Password
  static async forgotPassword(data: ResetPasswordRequest, signal?: AbortSignal): Promise<void> {
    return apiPost(`${this.BASE_URL}/forgot-password`, data, { signal });
  }

  // Reset Password
  static async resetPassword(data: SetPasswordRequest, signal?: AbortSignal): Promise<void> {
    return apiPost(`${this.BASE_URL}/reset-password`, data, { signal });
  }

  // Change Password (authenticated)
  static async changePassword(data: ChangePasswordRequest, signal?: AbortSignal): Promise<void> {
    return apiPost(`${this.BASE_URL}/profile/password`, data, { signal });
  }

  // Refresh Token
  static async refreshToken(refreshToken: string, signal?: AbortSignal): Promise<LoginResponse> {
    return apiPost<LoginResponse>(`${this.BASE_URL}/refresh-token`, {
      refreshToken,
    }, { signal });
  }

  // Get current authenticated user data
  static async getCurrentUser(signal?: AbortSignal): Promise<UserResponse> {
    return apiGet<UserResponse>(`${this.BASE_URL}/me`, { signal });
  }

  // Logout (clear local storage)
  static logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
  }
}